import { useState, useEffect, useCallback } from 'preact/hooks';
import { Sparkles, KeyRound, Check, Loader2, Infinity as InfinityIcon, ArrowUpRight, Plus, TriangleAlert } from 'lucide-preact';
import { t } from '../lib/i18n';
import { PomelaCheckoutModal } from './PomelaCheckout';

// §money-layer — the unified credit balance surface. Reads GET /v1/credits and
// shows ONE opaque balance: never the build/interaction conversion, never a
// price list. A calm meter under 75%; a tier-aware graduated banner at
// 75/90/100 (Free → "Upgrade to Pro", Pro → "Buy more credits"); a clear blocked
// state at 0; a green "your key — unlimited" on BYOK. Pro creators get a top-up
// button; free creators see the upgrade path instead.

interface Credits {
  tier: 'free' | 'pro';
  byok: boolean;
  credits: { granted: number; used: number; remaining: number };
  utilization: number; // 0–100
  alerts: number[]; // thresholds fired this cycle (75/90/100)
  canTopup: boolean;
}

type Phase = { k: 'loading' } | { k: 'ready' } | { k: 'saving' } | { k: 'error'; msg: string };

export function CreditPanel() {
  const [data, setData] = useState<Credits | null>(null);
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });
  const [keyDraft, setKeyDraft] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/v1/credits', { credentials: 'include' });
      if (!res.ok) { setPhase({ k: 'error', msg: t('cr.signIn') }); return; }
      setData((await res.json()) as Credits);
      setPhase({ k: 'ready' });
    } catch {
      setPhase({ k: 'error', msg: t('cr.loadErr') });
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const saveKey = async () => {
    if (keyDraft.trim().length < 8) return;
    setPhase({ k: 'saving' });
    try {
      const res = await fetch('/v1/ai/byok', { method: 'PUT', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ value: keyDraft.trim() }) });
      if (!res.ok) { setPhase({ k: 'error', msg: t('cr.errKeyRejected') }); return; }
      setKeyDraft(''); setShowKeyInput(false);
      await load();
    } catch { setPhase({ k: 'error', msg: t('cr.errSaveKey') }); }
  };
  const removeKey = async () => {
    setPhase({ k: 'saving' });
    try { await fetch('/v1/ai/byok', { method: 'DELETE', credentials: 'include' }); await load(); }
    catch { setPhase({ k: 'error', msg: t('cr.errRemoveKey') }); }
  };
  // §LG2 — top-ups run through the EMBEDDED Pomela Checkout (PayPal-processed,
  // server-created order, server-verified capture, idempotent grant). The old
  // blind redirect left approved orders UNCAPTURED (never charged, never granted).
  const [showCheckout, setShowCheckout] = useState(false);
  const buyCredits = () => setShowCheckout(true);

  const exhausted = !!data && !data.byok && data.credits.remaining <= 0;
  const alert = data && !data.byok ? Math.max(0, ...data.alerts) : 0; // highest fired threshold
  const pct = data ? Math.min(100, data.utilization) : 0;
  const meterColor = exhausted || pct >= 90 ? 'var(--color-danger,#E11D48)' : pct >= 75 ? 'var(--color-warning,#F59E0B)' : 'var(--color-primary)';
  const upgradeCta = data?.tier === 'pro';

  return (
    <section data-testid="credit-panel" className="surface flex flex-col gap-4 p-5 animate-vs-fade-in" aria-label="Credits">
      <header className="flex items-center gap-2.5">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--color-primary)]/12 text-[color:var(--color-primary)]">
          <Sparkles size={14} strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight text-[color:var(--fg)]">{t('cr.title')}</h2>
          <p className="text-[12px] leading-tight text-[color:var(--fg-muted)]">{t('cr.sub')}</p>
        </div>
      </header>

      {phase.k === 'loading' && <div className="h-16 rounded-[var(--radius-md)] bg-[color:var(--bg-subtle)] animate-pulse" aria-busy="true" />}

      {phase.k === 'error' && (
        <p className="rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-subtle)] px-3 py-2.5 text-[13px] text-[color:var(--fg-muted)]">{phase.msg}</p>
      )}

      {data && phase.k !== 'loading' && phase.k !== 'error' && (
        <>
          {data.byok ? (
            <div className="flex items-center justify-between rounded-[var(--radius-lg)] border border-[color:var(--color-success,#10B981)]/30 bg-[color:var(--color-success,#10B981)]/[0.07] px-4 py-3">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--color-success,#10B981)]/15 text-[color:var(--color-success,#0F9D6E)]"><KeyRound size={15} /></span>
                <div>
                  <p className="text-[13px] font-semibold text-[color:var(--fg)] flex items-center gap-1">{t('cr.yourKey')} <InfinityIcon size={13} className="text-[color:var(--color-success,#0F9D6E)]" /> {t('cr.unlimited')}</p>
                  <p className="text-[12px] text-[color:var(--fg-muted)]">{t('cr.keyBilled')}</p>
                </div>
              </div>
              <button type="button" onClick={removeKey} disabled={phase.k === 'saving'} className="inline-flex h-9 items-center rounded-[var(--radius-md)] px-2.5 text-[12px] font-medium text-[color:var(--fg-subtle)] transition hover:text-[color:var(--fg)] disabled:opacity-50">{t('cr.remove')}</button>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {/* Single opaque balance — credits remaining, never a build count. */}
              <div className="flex items-baseline justify-between">
                <span className="text-[13px] font-medium text-[color:var(--fg-muted)]">{t('cr.left')}</span>
                <span className="text-[15px] font-semibold tabular-nums text-[color:var(--fg)]">{data.credits.remaining.toLocaleString()} <span className="text-[13px] font-medium text-[color:var(--fg-subtle)]">/ {data.credits.granted.toLocaleString()}</span></span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[color:var(--bg-subtle)]">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: meterColor }} />
              </div>

              {exhausted ? (
                <div className="mt-1 flex flex-col gap-2 rounded-[var(--radius-md)] border border-[color:var(--border-strong)] bg-[color:var(--bg-subtle)] p-3">
                  <p className="text-[13px] font-semibold text-[color:var(--fg)]">{t('cr.exhausted')}</p>
                  <p className="text-[12px] text-[color:var(--fg-muted)]">{upgradeCta ? t('cr.topupHint') : t('cr.upgradeHint')}</p>
                  <div className="mt-0.5 flex items-center gap-2">
                    {upgradeCta ? (
                      <button type="button" onClick={buyCredits} disabled={phase.k === 'saving'} className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-3 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[color:var(--color-primary-hover)] active:scale-[0.98] disabled:opacity-50">{phase.k === 'saving' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {t('cr.buy')}</button>
                    ) : (
                      <a href="#upgrade" className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-3 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[color:var(--color-primary-hover)] active:scale-[0.98]">{t('cr.upgradeCta')} <ArrowUpRight size={13} /></a>
                    )}
                    <button type="button" onClick={() => setShowKeyInput(true)} className="inline-flex h-10 items-center justify-center gap-1 rounded-[var(--radius-md)] border border-[color:var(--border)] px-3 text-[13px] font-medium text-[color:var(--fg)] transition hover:bg-[color:var(--bg-elevated)]"><KeyRound size={13} /> {t('cr.useKey')}</button>
                  </div>
                </div>
              ) : alert >= 75 ? (
                <div className={`mt-1 flex items-start gap-2.5 rounded-[var(--radius-md)] border p-3 ${alert >= 100 ? 'border-[color:var(--color-danger,#E11D48)]/35 bg-[color:var(--color-danger,#E11D48)]/[0.06]' : 'border-[color:var(--color-warning,#F59E0B)]/35 bg-[color:var(--color-warning,#F59E0B)]/[0.07]'}`}>
                  <TriangleAlert size={15} className={alert >= 100 ? 'mt-0.5 shrink-0 text-[color:var(--color-danger,#E11D48)]' : 'mt-0.5 shrink-0 text-[color:var(--color-warning,#F59E0B)]'} />
                  <div className="flex flex-col gap-1.5">
                    <p className="text-[12.5px] leading-snug text-[color:var(--fg)]"><span className="font-semibold">{t('cr.used', { pct: alert })}</span> {upgradeCta ? t('cr.alertTopup') : t('cr.alertUpgrade')}</p>
                    {upgradeCta ? (
                      <button type="button" onClick={buyCredits} disabled={phase.k === 'saving'} className="self-start inline-flex h-8 items-center gap-1 rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-2.5 text-[12px] font-semibold text-white transition hover:bg-[color:var(--color-primary-hover)] disabled:opacity-50"><Plus size={12} /> {t('cr.buyMore')}</button>
                    ) : (
                      <a href="#upgrade" className="self-start inline-flex h-8 items-center gap-1 rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-2.5 text-[12px] font-semibold text-white transition hover:bg-[color:var(--color-primary-hover)]">{t('cr.upgradeCta')} <ArrowUpRight size={12} /></a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  {data.canTopup ? (
                    <button type="button" onClick={buyCredits} disabled={phase.k === 'saving'} className="inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--color-primary)] transition hover:underline disabled:opacity-50"><Plus size={12} /> {t('cr.buyMore')}</button>
                  ) : (
                    <a href="#upgrade" className="inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--color-primary)] transition hover:underline">{t('cr.upgradeMore')} <ArrowUpRight size={12} /></a>
                  )}
                  <button type="button" onClick={() => setShowKeyInput((s) => !s)} className="text-[12px] font-medium text-[color:var(--fg-subtle)] transition hover:text-[color:var(--fg)]">{t('cr.ownKey')}</button>
                </div>
              )}
            </div>
          )}

          {showKeyInput && !data.byok && (
            <div className="flex flex-col gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border)] bg-[color:var(--bg-subtle)] p-3.5 animate-vs-fade-in">
              <label className="text-[12px] font-medium text-[color:var(--fg-muted)]">{t('cr.keyLabel')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={keyDraft}
                  onInput={(e) => setKeyDraft((e.target as HTMLInputElement).value)}
                  placeholder="sk-ant-…"
                  style={{ outline: 'none', '--tw-ring-color': 'color-mix(in srgb, var(--color-primary) 30%, transparent)' } as Record<string, string>}
                  className="min-h-[44px] flex-1 rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-3 text-[14px] text-[color:var(--fg)] outline-none transition focus:border-[color:var(--color-primary)] focus:ring-2"
                  aria-label={t('cr.ariaKey')}
                />
                <button type="button" onClick={saveKey} disabled={phase.k === 'saving' || keyDraft.trim().length < 8} className="inline-flex h-11 items-center gap-1.5 rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-3.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[color:var(--color-primary-hover)] active:scale-95 disabled:opacity-50">
                  {phase.k === 'saving' ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} {t('cr.save')}
                </button>
              </div>
              <p className="text-[11px] text-[color:var(--fg-subtle)]">{t('cr.keyStored')}</p>
            </div>
          )}
        </>
      )}

      <PomelaCheckoutModal
        mode="topup"
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        onDone={() => { void load(); }}
      />
    </section>
  );
}
